'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, X, Send } from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'ai';
  text: string;
}

const SUGGESTED = [
  'Cheapest vegetables today?',
  'Best buys under ₹50/kg?',
  'Build a ₹500 family basket',
  'Why did tomato price drop?',
  'Best fruits for summer heat?',
];

const AI_RESPONSES: Record<string, string> = {
  'Cheapest vegetables today?': '🥬 Today\'s cheapest vegetables:\n1. Mint Leaves ₹5/kg\n2. Spinach ₹14/kg\n3. Coriander ₹15/kg\n4. Raw Mango ₹14/kg\n5. Pumpkin ₹21/kg\n\nAll fresh and available at Koyambedu right now.',
  'Best buys under ₹50/kg?': '💚 Great value picks under ₹50:\n• Cucumber ₹27 — Hydrating, summer perfect\n• Potato ₹27 — Versatile staple\n• Spinach ₹14 — Nutrition powerhouse\n• Tomato ₹35 — Down 27% this week!\n• Onion Big ₹26 — Steady supply',
  'Build a ₹500 family basket': '🛒 Smart ₹500 Family Basket:\n• Tomato 2kg — ₹70\n• Onion 2kg — ₹52\n• Potato 2kg — ₹54\n• Cucumber 2kg — ₹54\n• Greens mix 1kg — ₹29\n• Beans 0.5kg — ₹38\n• Garlic 0.2kg — ₹25\n\nTotal: ₹322 — Saves ₹178 for extras!',
  'Why did tomato price drop?': '📊 Tomato dropped 27% this week because:\n• Good harvest from Krishnagiri & Hosur regions\n• Reduced transport disruptions on NH48\n• Seasonal supply peak for June\n• Current price ₹35/kg — down from ₹62 last week\n\nGood time to stock up!',
  'Best fruits for summer heat?': '☀️ Beat the heat with these fruits:\n• Raw Mango ₹14/kg — Electrolytes, perfect for summer\n• Cucumber ₹27/kg — 96% water content\n• Watermelon (arriving tomorrow) — Cooling\n• Banana ₹32/kg — Instant energy\n\nAll available fresh at Koyambedu today.',
};

export default function AIChatFab() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { id: 'msg-init', role: 'ai', text: 'நமஸ்காரம்! 🌿 I\'m your Koyambedu Market Guide. Ask me about today\'s prices, best buys, or seasonal picks!' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  const sendMessage = (text: string) => {
    if (!text.trim() || loading) return;
    const userMsg: Message = { id: `msg-u-${Date.now()}`, role: 'user', text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // Backend integration point: replace with actual AI API call
    setTimeout(() => {
      const response = AI_RESPONSES[text] || `I found some data for "${text}". Prices at Koyambedu today are fresh as of 6:30 AM. Try asking about specific vegetables or fruits for detailed pricing!`;
      const aiMsg: Message = { id: `msg-a-${Date.now()}`, role: 'ai', text: response };
      setMessages(prev => [...prev, aiMsg]);
      setLoading(false);
    }, 1200);
  };

  return (
    <>
      {/* FAB Button */}
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-24 md:bottom-8 right-4 md:right-8 z-40 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-float flex items-center justify-center pulse-green transition-all duration-200 hover:opacity-90 active:scale-95 ${open ? 'hidden' : 'flex'}`}
        aria-label="Open AI Market Guide"
      >
        <Sparkles size={22} />
      </button>

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-24 md:bottom-8 right-4 md:right-8 z-50 w-[calc(100vw-2rem)] max-w-sm bg-card rounded-3xl shadow-float border border-border scale-in flex flex-col overflow-hidden" style={{ height: '480px' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary-foreground/20 rounded-xl flex items-center justify-center">
                <Sparkles size={16} />
              </div>
              <div>
                <p className="font-semibold text-sm">AI Market Guide</p>
                <p className="text-xs opacity-75">Powered by Koyambedu data</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-primary-foreground/20 hover:bg-primary-foreground/30 transition-all"
              aria-label="Close AI chat"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide">
            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-line ${
                  msg.role === 'user' ?'bg-primary text-primary-foreground rounded-br-sm' :'bg-secondary text-foreground rounded-bl-sm'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-secondary rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1.5">
                  {[0, 1, 2].map(i => (
                    <span key={`dot-${i}`} className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions */}
          <div className="px-3 pb-2 flex gap-2 overflow-x-auto scrollbar-hide">
            {SUGGESTED.map(s => (
              <button
                key={`sug-${s}`}
                onClick={() => sendMessage(s)}
                className="flex-shrink-0 text-xs font-medium bg-secondary text-primary rounded-full px-3 py-1.5 hover:bg-green-light transition-all active:scale-95"
              >
                {s}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="px-3 pb-3 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage(input)}
              placeholder="Ask about prices, recipes..."
              className="input-base text-sm py-2.5"
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
              className="w-10 h-10 flex-shrink-0 bg-primary text-primary-foreground rounded-xl flex items-center justify-center transition-all hover:opacity-90 active:scale-95 disabled:opacity-40"
              aria-label="Send message"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}